import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { MenuSection, AppTheme } from '../types';
import { useLanguage } from '../contexts/LanguageContext';
import { MENU_ID_TO_ROUTE } from '../config/routes';
import './Sidebar.css';

interface SidebarProps {
  theme: AppTheme;
  onMenuItemClick?: (itemId: string) => void;
}

const getMenuIcon = (id: string) => {
  const iconMap: { [key: string]: string } = {
    'cashier-workplace': '🖥️',
    'product-rating': '⭐',
    'average-check': '💳',
    'sales-analysis': '📈',
    'stock-balance': '📦',
    'cash-balance': '💰',
    'receipt': '📥',
    'sale': '💰',
    'write-off': '📤',
    'return-from-customer': '↩️',
    'banks': '🏦',
    'bank-accounts': '💳',
    'warehouses': '🏬',
    'product-categories': '📂',
    'contractors': '🏢',
    'contracts': '📝',
    'products': '📦',
    'countries': '🌍',
    'gekd-codes': '🔢',
    'tnved-codes': '📋',
    'units': '📏'
  };
  return iconMap[id] || '📄';
};

const getMenuData = (t: (key: string) => string): MenuSection[] => [
  {
    title: t('menu.section.cashier'),
    items: [
      { id: 'cashier-workplace', title: t('menu.cashier-workplace') }
    ]
  },
  {
    title: t('menu.section.reports'),
    items: [
      { id: 'product-rating', title: t('menu.product-rating') },
      { id: 'average-check', title: t('menu.average-check') },
      { id: 'sales-analysis', title: t('menu.sales-analysis') },
      { id: 'stock-balance', title: t('menu.stock-balance') },
      { id: 'cash-balance', title: t('menu.cash-balance') }
    ]
  },
  {
    title: t('menu.section.operations'),
    items: [
      { id: 'receipt', title: t('menu.receipt') },
      { id: 'sale', title: t('menu.sale') },
      { id: 'write-off', title: t('menu.write-off') },
      { id: 'return-from-customer', title: t('menu.return-from-customer') }
    ]
  },
  {
    title: t('menu.section.references'),
    items: [
      { id: 'bank-accounts', title: t('menu.bank-accounts') },
      { id: 'warehouses', title: t('menu.warehouses') },
      { id: 'product-categories', title: t('menu.product-categories') },
      { id: 'contractors', title: t('menu.contractors') },
      { id: 'contracts', title: t('menu.contracts') },
      { id: 'products', title: t('menu.products') }
    ]
  },
  {
    title: t('menu.section.static'),
    items: [
      { id: 'banks', title: t('menu.banks') },
      { id: 'countries', title: t('menu.countries') },
      { id: 'gekd-codes', title: t('menu.gekd-codes') },
      { id: 'tnved-codes', title: t('menu.tnved-codes') },
      { id: 'units', title: t('menu.units') }
    ]
  }
];

export const Sidebar: React.FC<SidebarProps> = ({ theme, onMenuItemClick }) => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const [expandedSections, setExpandedSections] = useState<string[]>([]);
  const menuData = getMenuData(t);

  const toggleSection = (sectionTitle: string) => {
    setExpandedSections(prev =>
      prev.includes(sectionTitle)
        ? prev.filter(s => s !== sectionTitle)
        : [...prev, sectionTitle]
    );
  };

  const handleMenuItemClick = (itemId: string) => {
    const route = MENU_ID_TO_ROUTE[itemId];
    if (route) {
      navigate(route);
    }
    if (onMenuItemClick) {
      onMenuItemClick(itemId);
    }
  };

  return (
    <div className={`sidebar ${theme.isDark ? 'dark' : 'light'}`}>
      <div className={`sidebar-header ${theme.isDark ? 'dark' : 'light'}`}>
        <img
          src={theme.isDark ? "/bakai_dark.png" : "/bakai.png"}
          alt="Bakai Bank"
          className="sidebar-logo"
        />
      </div>
      
      {menuData.map((section) => {
        const isExpanded = expandedSections.includes(section.title);
        return (
          <div key={section.title} className="menu-section">
            <div
              className={`menu-section-header ${theme.isDark ? 'dark' : 'light'}`}
              onClick={() => toggleSection(section.title)}
            >
              <span>{section.title}</span>
              <span className={`menu-section-arrow ${isExpanded ? 'expanded' : ''}`}>
                ▶
              </span>
            </div>
            <div className={`menu-items ${isExpanded ? 'expanded' : ''}`}>
              {section.items.map((item) => {
                const route = MENU_ID_TO_ROUTE[item.id];
                const isActive = route && location.pathname === route;
                return (
                  <div
                    key={item.id}
                    className={`menu-item ${theme.isDark ? 'dark' : 'light'} ${isActive ? 'active' : ''}`}
                    onClick={() => handleMenuItemClick(item.id)}
                  >
                    <span>{getMenuIcon(item.id)}</span>
                    <span>{item.title}</span>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
};